import { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BookOpen,
  FileText,
  Video,
  Clock,
  CheckCircle,
  Lock,
  ArrowRight,
  Users,
  Calculator,
  Calendar,
  MessageCircle,
  Unlock,
  Wallet,
  ClipboardCheck,
  PiggyBank,
  Table,
  Mail,
  Loader2,
  Sparkles,
  TrendingUp,
  Star,
  Eye,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import MentorshipBookingModal from "@/components/MentorshipBookingModal";
import TaxFilingModal from "@/components/TaxFilingModal";
import AuthModal from "@/components/AuthModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [mentorshipModalOpen, setMentorshipModalOpen] = useState(false);
  const [taxFilingModalOpen, setTaxFilingModalOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const userToken = localStorage.getItem("user_token") || localStorage.getItem("token");
    const userData = localStorage.getItem("user_data") || localStorage.getItem("user");
    const sessionToken = sessionStorage.getItem("user_token");
    const sessionUser = sessionStorage.getItem("user_data");
    
    if (userToken || sessionToken) {
      setIsUnlocked(true);
    }
    if (userData) {
      try {
        setUser(JSON.parse(userData));
        setIsUnlocked(true);
      } catch (e) {
        console.error("Failed to parse user data");
      }
    } else if (sessionUser) {
      try {
        setUser(JSON.parse(sessionUser));
        setIsUnlocked(true);
      } catch (e) {
        console.error("Failed to parse session user data");
      }
    }
  }, []);

  // Handle auth success
  const handleAuthSuccess = (userData) => {
    localStorage.setItem("user_token", userData.token || "authenticated");
    localStorage.setItem("compoundr_subscribed", "true");
    setIsUnlocked(true);
    setShowAuth(false);
    window.location.reload();
  };

  // Scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Please enter your email");
      return;
    }

    setNewsletterLoading(true);
    try {
      await axios.post(`${API_URL}/api/subscribers`, {
        email: newsletterEmail,
        phone: "",
        source: "newsletter_products",
      });
      setNewsletterSuccess(true);
      setNewsletterEmail("");
      toast.success("Welcome to the Compoundr newsletter! 🎉");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.success("You're already subscribed! We'll keep you updated.");
        setNewsletterSuccess(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  // Investment Tools
  const investmentTools = [
    {
      title: "Stock Market Simulator",
      description: "Get $10,000 in virtual cash to build and manage your portfolio. Track real-time prices for 200+ stocks and ETFs, compete on the leaderboard, and learn by doing.",
      icon: TrendingUp,
      color: "from-emerald-500 to-teal-600",
      features: ["$10,000 virtual cash", "200+ stocks & ETFs", "Real-time prices", "Public leaderboard", "CAD/USD support"],
      cta: user ? "Go to My Portfolio" : "Start Investing",
      link: "/tools?tool=simulator",
      badge: "Most Popular",
      badgeColor: "bg-amber-500",
    },
    {
      title: "Daily Market Update",
      description: "AI-powered daily market summary with top gainers, losers, and founder commentary. Stay informed without the overwhelm.",
      icon: BarChart3,
      color: "from-blue-500 to-indigo-600",
      features: ["Real-time market data", "S&P 500 & TSX coverage", "Beginner-friendly tips", "Founder insights"],
      cta: "View Today's Update",
      link: "/tools?tool=market-update",
      badge: "AI Powered",
      badgeColor: "bg-purple-500",
    },
    {
      title: "Learn Hub",
      description: "Master 105+ financial terms with AI-powered explanations, interactive quizzes, and natural language search. Your personal finance tutor.",
      icon: BookOpen,
      color: "from-purple-500 to-indigo-600",
      features: ["105+ financial terms", "AI chat assistant", "Interactive quizzes", "Natural language search"],
      cta: "Start Learning",
      link: "/tools?tool=learnhub",
      badge: "Educational",
      badgeColor: "bg-indigo-500",
    },
  ];

  const freeGuides = [
    {
      title: "Welcome to Investing Starter Pack",
      description:
        "Your complete guide to getting started with investing: opening a bank account, getting your SIN, and setting up an investment account.",
      type: "PDF Guide",
      icon: BookOpen,
      available: true,
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/ftyw4y4y_Compoundr_Investors_Guide_Final_v2.pdf",
    },
    {
      title: "TFSA Starter Guide",
      description:
        "Everything you need to know about Tax-Free Savings Accounts in Canada.",
      type: "PDF Guide",
      icon: PiggyBank,
      available: true,
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/e6ky3w2i_TFSA_Starter_Guide%20%282%29.pdf",
    },
    {
      title: "Student Budget Template",
      description:
        "A simple spreadsheet to track your income, expenses, and investments.",
      type: "Spreadsheet",
      icon: Table,
      available: true,
      downloadUrl: "https://customer-assets.emergentagent.com/job_etf-advisor/artifacts/d0dp736a_Student_Budget_Template.xlsx",
    },
    {
      title: "First Investment Checklist",
      description:
        "Step-by-step checklist to make your first investment with confidence.",
      type: "Checklist",
      icon: ClipboardCheck,
      available: true,
      downloadUrl: "https://customer-assets.emergentagent.com/job_etf-advisor/artifacts/nc6jnk17_First_Investment_Checklist%20%284%29.pdf",
    },
  ];

  const booksBundle = [
    {
      title: "The Intelligent Investor",
      author: "Benjamin Graham",
      description: "The definitive book on value investing. A must-read for every serious investor.",
      filename: "The-Intelligent-Investor.pdf",
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/6njhxix9_The%20Intelligent%20Investor%20-%20BENJAMIN%20GRAHAM.pdf",
    },
    {
      title: "The Psychology of Money",
      author: "Morgan Housel",
      description: "Timeless lessons on wealth, greed, and happiness. Perfect for beginners.",
      filename: "The-Psychology-of-Money.pdf",
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/q09d5yma_The%20psycology%20of%20money.pdf",
    },
    {
      title: "Rich Dad Poor Dad",
      author: "Robert Kiyosaki",
      description: "Learn the mindset differences between the rich and the poor.",
      filename: "Rich-Dad-Poor-Dad.pdf",
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/f1ynod4z_20150408135309188.pdf",
    },
    {
      title: "I Will Teach You to Be Rich",
      author: "Ramit Sethi",
      description: "A practical guide to managing money, investing, and building wealth in your 20s and 30s.",
      filename: "I-Will-Teach-You-To-Be-Rich.pdf",
      downloadUrl: "https://customer-assets.emergentagent.com/job_finance-tools-31/artifacts/peta4loz_pdfcoffee.com-i-will-teach-you-to-be-rich.pdf",
    },
  ];

  const services = [
    {
      title: "One-on-One Mentorship",
      description:
        "Get a chance to meet an investment expert who will help you build your portfolio and guide you through your investing journey.",
      disclaimer: "(not investment advice)",
      icon: Users,
      color: "from-emerald-500 to-emerald-600",
      features: [
        "Meet with an investment expert",
        "Portfolio building guidance",
        "Goal setting & tracking",
        "Ongoing support via chat",
      ],
      cta: "Book a Session",
      available: true,
    },
    {
      title: "File Your Taxes",
      description:
        "We connect you to a premium accountant. A Certified CPA who will help you maximize your refund and ensure you claim all eligible credits.",
      icon: Calculator,
      color: "from-purple-500 to-purple-600",
      features: [
        "Certified CPA accountant",
        "Maximize your refund",
        "International student expertise",
        "Fast & accurate filing",
      ],
      cta: "Get Started",
      available: true,
    },
  ];

  const upcomingPrograms = [
    {
      title: "Compoundr Bootcamp",
      description:
        "A 4-week guided program to go from complete beginner to confident investor. Live sessions, community support, and personalized feedback.",
      duration: "4 weeks",
      format: "Live + Self-paced",
      status: "Coming Soon",
      features: [
        "Weekly live Q&A sessions",
        "Step-by-step video lessons",
        "Private community access",
        "1-on-1 portfolio review",
      ],
    },
    {
      title: "International Student Money Mastery",
      description:
        "Specifically designed for international students in Canada. Learn to navigate banking, investing, and building credit as a newcomer.",
      duration: "3 weeks",
      format: "Self-paced",
      status: "Coming Soon",
      features: [
        "Bank account setup guide",
        "Credit building strategies",
        "Currency exchange tips",
        "Tax basics for students",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="products-page">
      {/* Header */}
      <section className="py-16 lg:py-24 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-emerald-600 hover:text-emerald-700 mb-6 text-sm font-medium">
            ← Back to Home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">
              Tools & Resources
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-navy-700 mb-4">
              Products & Guides
            </h1>
            <p className="text-lg text-slate-600">
              Free resources to get started, and structured programs when you're
              ready to level up.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Investment Tools - NEW SECTION */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <Badge className="bg-emerald-100 text-emerald-700 mb-2">
              <Sparkles className="w-3 h-3 mr-1" />
              Interactive Tools
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
              Investment Tools
            </h2>
            <p className="text-slate-600">
              Practice investing risk-free and stay informed with our interactive tools.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {investmentTools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <motion.div
                  key={tool.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={`h-full overflow-hidden border-0 shadow-lg bg-gradient-to-br ${tool.color} text-white relative`}>
                    <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <CardContent className="p-8 relative">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <Badge className={`${tool.badgeColor} text-white`}>
                          {tool.badge}
                        </Badge>
                      </div>
                      <h3 className="text-2xl font-heading font-bold mb-2">
                        {tool.title}
                      </h3>
                      <p className="text-white/90 mb-6">
                        {tool.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {tool.features.map((feature, i) => (
                          <Badge key={i} className="bg-white/20 text-white text-xs">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                      <Button
                        onClick={() => navigate(tool.link)}
                        className="bg-white text-slate-800 hover:bg-white/90"
                        data-testid={`tool-${tool.title.toLowerCase().replace(/ /g, "-")}`}
                      >
                        {tool.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Free Guides */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
              Free Guides
            </h2>
            <p className="text-slate-600">
              Download these resources to start learning right away.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {freeGuides.map((guide, index) => (
              <motion.div
                key={guide.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full card-hover border-0 shadow-card bg-white">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                      <guide.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <Badge variant="secondary" className="mb-3">
                      {guide.type}
                    </Badge>
                    <h3 className="font-heading font-bold text-lg text-navy-700 mb-2">
                      {guide.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">
                      {guide.description}
                    </p>
                    {guide.downloadUrl ? (
                      isUnlocked ? (
                        <a 
                          href={guide.downloadUrl}
                          download
                          className="w-full"
                        >
                          <Button
                            variant="outline"
                            className="w-full"
                            data-testid={`download-${guide.title.toLowerCase().replace(/ /g, "-")}`}
                          >
                            Download Free
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </a>
                      ) : (
                        <Button
                          variant="outline"
                          className="w-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                          onClick={() => setShowAuth(true)}
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Sign in to Download
                        </Button>
                      )
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full"
                        disabled={!guide.available}
                      >
                        {guide.available ? (
                          <>
                            Download Free
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        ) : (
                          <>
                            <Lock className="w-4 h-4 mr-2" />
                            Coming Soon
                          </>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Investor's Book Bundle */}
      <section id="books" className="py-16 lg:py-24 bg-navy-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-white/20 text-white mb-4">
              <Sparkles className="w-3 h-3 mr-1" />
              Limited Time Offer
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              The Investor's Book Bundle
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Get instant access to 4 must-read investing books that have shaped millions of successful investors.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {booksBundle.map((book, index) => (
              <motion.div
                key={book.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white/10 backdrop-blur border-white/20 text-white">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                      <BookOpen className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-1">
                      {book.title}
                    </h3>
                    <p className="text-emerald-400 text-sm mb-2">by {book.author}</p>
                    <p className="text-slate-300 text-sm">
                      {book.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white/10 backdrop-blur rounded-2xl p-6 sm:p-8">
              <div className="text-center sm:text-left">
                <p className="text-slate-300 line-through">Value: $80+</p>
                <p className="text-4xl font-bold text-white">Free Download</p>
                <p className="text-emerald-400 text-sm">All 4 books included</p>
              </div>
              <div className="flex flex-col gap-2">
                {isUnlocked ? (
                  booksBundle.map((book) => (
                    <a 
                      key={book.title}
                      href={book.downloadUrl}
                      download
                    >
                      <Button 
                        className="bg-emerald-500 hover:bg-emerald-600 text-white w-full"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        {book.title.split(' ').slice(0, 3).join(' ')}...
                      </Button>
                    </a>
                  ))
                ) : (
                  <Button 
                    className="bg-slate-500 text-white cursor-not-allowed"
                    disabled
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Sign in to Download All
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
              Premium Services
            </h2>
            <p className="text-slate-600">
              Get personalized help from our expert team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full overflow-hidden border-0 shadow-lg bg-gradient-to-br ${service.color} text-white`}>
                  <CardContent className="p-8">
                    <service.icon className="w-12 h-12 mb-4" />
                    <h3 className="text-2xl font-heading font-bold mb-2">
                      {service.title}
                    </h3>
                    <p className="text-white/80 mb-4">
                      {service.description}
                      {service.disclaimer && (
                        <span className="text-white/60 text-xs block mt-1">
                          {service.disclaimer}
                        </span>
                      )}
                    </p>
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-white/80" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={isUnlocked ? "bg-white text-slate-800 hover:bg-white/90" : "bg-slate-300 text-slate-500 cursor-not-allowed"}
                      disabled={!isUnlocked || !service.available}
                      onClick={() => {
                        if (!isUnlocked) return;
                        if (service.title === "One-on-One Mentorship") {
                          setMentorshipModalOpen(true);
                        } else if (service.title === "File Your Taxes") {
                          setTaxFilingModalOpen(true);
                        }
                      }}
                    >
                      {isUnlocked ? (
                        <>
                          {service.cta}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          Sign in to Access
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Programs */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-purple-100 text-purple-700 mb-4">
              Coming Soon
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
              Upcoming Programs
            </h2>
            <p className="text-slate-600">
              Structured learning experiences launching soon.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {upcomingPrograms.map((program, index) => (
              <motion.div
                key={program.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-2 border-dashed border-slate-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Badge variant="secondary">{program.status}</Badge>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {program.duration}
                        </span>
                        <span>{program.format}</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">
                      {program.title}
                    </h3>
                    <p className="text-slate-600 mb-4">{program.description}</p>
                    <ul className="space-y-2">
                      {program.features.map((feature, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-sm text-slate-600"
                        >
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 lg:py-24 bg-emerald-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mail className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-4">
            Stay in the Loop
          </h2>
          <p className="text-slate-600 mb-8">
            Get notified when new programs launch and receive exclusive investing tips.
          </p>
          
          {newsletterSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-emerald-100 p-6 rounded-xl"
            >
              <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
              <p className="text-emerald-800 font-medium">You&apos;re on the list!</p>
              <p className="text-emerald-600 text-sm">We&apos;ll notify you about new blogs, market updates, content, and AI tools!</p>
            </motion.div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" disabled={newsletterLoading}>
                {newsletterLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Chatbot */}
      

      {/* Mentorship Modal */}
      <MentorshipBookingModal
        open={mentorshipModalOpen}
        onOpenChange={setMentorshipModalOpen}
      />

      {/* Tax Filing Modal */}
      <TaxFilingModal
        open={taxFilingModalOpen}
        onOpenChange={setTaxFilingModalOpen}
      />

      {/* Auth Modal for locked content */}
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={handleAuthSuccess}
        required={true}
      />
    </div>
  );
};

export default Products;
